import { useRouter } from 'next/router';

import useConfig from 'src/common/hooks/useConfig';

export const constructHref = ({
  base,
  basename,
  asPath,
}: {
  base: string;
  basename?: string;
  asPath: string;
}): string => {
  const baseNameWithLeadingSlash = basename?.startsWith('/')
    ? basename
    : `/${basename}`;
  const baseName =
    !basename || basename === '/'
      ? ''
      : baseNameWithLeadingSlash.replace(/\/$/, '');

  if (!baseName) return base + asPath;
  if (!asPath || asPath === '/') return base + baseName;
  if (asPath.startsWith('/?') || asPath.startsWith('/#')) {
    return base + baseName + asPath.slice(1);
  }
  if (
    asPath === baseName ||
    asPath.startsWith(`${baseName}/`) ||
    asPath.startsWith(`${baseName}?`) ||
    asPath.startsWith(`${baseName}#`)
  ) {
    return base + asPath;
  }

  return base + baseName + (asPath.startsWith('/') ? asPath : `/${asPath}`);
};

export const constructHrefWithoutSearchAndHash = ({
  base,
  basename,
  asPath,
  hash,
  encode,
}: {
  base: string;
  basename?: string;
  asPath: string;
  hash?: string;
  encode?: boolean;
}): string => {
  const urlString = constructHref({ base, basename, asPath });
  const url = new URL(urlString);
  url.hash = '';
  url.search = '';
  if (hash) url.hash = hash;
  return encode ? encodeURIComponent(url.href) : url.href;
};

export const useCurrentHrefWithoutSearchAndHash = (
  { hash, encode }: { hash?: string; encode?: boolean } = { encode: true }
) => {
  const router = useRouter();
  const config = useConfig();
  return constructHrefWithoutSearchAndHash({
    base: config.CANDIDATE_BASE,
    basename: config.BASENAME,
    asPath: router.asPath,
    hash,
    encode,
  });
};

export const useCurrentHref = () => {
  const router = useRouter();
  const config = useConfig();
  return constructHref({
    base: config.CANDIDATE_BASE,
    basename: config.BASENAME,
    asPath: router.asPath,
  });
};

export const useHostNameAndBaseName = (): string => {
  const config = useConfig();
  const baseName = config.BASENAME === '/' ? '' : config.BASENAME;
  return config.CANDIDATE_BASE + baseName;
};
